<template>
  <div class="landing-page">

    <header>
        <nav>
            <div class="logo">ProductsDB<span class="reg">®</span></div>
            <div class="nav-right">
                <ul class="nav-links">
                    <li><a href="#" @click.prevent="openModal('contact')">Contact</a></li>
                </ul>
                <!-- LOGIN URL — change here if the app login moves -->
                <router-link to="/login" class="login-link">Log in</router-link>
                <a href="#" @click.prevent="openModal('demo')" class="cta-button">Request Demo</a>
            </div>
        </nav>
    </header>

    <section class="hero">
        <div class="hero-content">
            <h1><span class="brand">ProductsDB<span class="reg">®</span></span><br>AI-Native Product Data Platform</h1>
            <p>Automation-first engine for product data enrichment, standardisation, and multi-channel syndication</p>
            <div class="hero-buttons">
                <a href="#" @click.prevent="openModal('demo')" class="btn-primary">Request Demo</a>
                <router-link to="/login" class="btn-secondary">Log in</router-link>
            </div>
        </div>
    </section>

    <section class="countdown-section">
        <h2>Launching Soon</h2>
        <p>ProductsDB<span class="reg">®</span> is coming soon. Be among the first to experience the future of product data management.</p>
        <div class="countdown">
            <div class="countdown-item">
                <span class="countdown-value">{{ days }}</span>
                <span class="countdown-label">Days</span>
            </div>
            <div class="countdown-item">
                <span class="countdown-value">{{ hours }}</span>
                <span class="countdown-label">Hours</span>
            </div>
            <div class="countdown-item">
                <span class="countdown-value">{{ minutes }}</span>
                <span class="countdown-label">Minutes</span>
            </div>
            <div class="countdown-item">
                <span class="countdown-value">{{ seconds }}</span>
                <span class="countdown-label">Seconds</span>
            </div>
        </div>
        <a href="#" @click.prevent="openModal('early-access')" class="btn-primary">Request Early Access</a>
    </section>
    <section class="features">
        <div class="features-content">
            <div class="section-header">
                <h2>Powerful Features for Modern Commerce</h2>
                <p>Transform your product data workflow with AI-powered automation</p>
            </div>
            <div class="features-grid">
                <div class="feature-card">
                    <div class="feature-icon">🤖</div>
                    <h3>AI-Native Enrichment</h3>
                    <p>Leverage advanced AI models to automatically enrich product data with accurate descriptions, attributes, and metadata.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">⚡</div>
                    <h3>Automation-First</h3>
                    <p>Built from the ground up for automation. Process thousands of products with minimal manual intervention.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">🎯</div>
                    <h3>Data Standardisation</h3>
                    <p>Ensure consistency across all product data with intelligent standardisation and validation rules.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">🔄</div>
                    <h3>Multi-Channel Syndication</h3>
                    <p>Seamlessly distribute product data across multiple channels, marketplaces, and platforms in real-time.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">📊</div>
                    <h3>Quality Insights</h3>
                    <p>Monitor data quality with comprehensive analytics and actionable insights to improve your product catalog.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">🔗</div>
                    <h3>Seamless Integration</h3>
                    <p>Connect with your existing systems through powerful APIs and pre-built integrations.</p>
                </div>
            </div>
        </div>
    </section>

    <section class="cta-section">
        <h2>Ready to Transform Your Product Data?</h2>
        <p>Join leading brands using ProductsDB<span class="reg">®</span> to streamline their product data operations</p>
        <a href="#" @click.prevent="openModal('demo')" class="btn-white">Request Demo</a>
    </section>

    <!-- Contact Form Modal -->
    <div class="modal" :class="{ active: modalOpen }" @click.self="closeModal">
        <div class="modal-content">
            <span class="close-modal" @click="closeModal">&times;</span>
            <h2>{{ modalTitle }}</h2>
            <p>{{ modalSubtitle }}</p>
            
            <div class="form-message" :class="{ active: !!formMsg, success: formOk, error: !!formMsg && !formOk }">{{ formMsg }}</div>
            
            <form @submit.prevent="handleSubmit">
                <div class="form-group">
                    <label for="name">Name *</label>
                    <input type="text" id="name" name="name" v-model="form.name" required>
                </div>
                
                <div class="form-group">
                    <label for="email">Email *</label>
                    <input type="email" id="email" name="email" v-model="form.email" required>
                </div>
                
                <div class="form-group">
                    <label for="company">Company</label>
                    <input type="text" id="company" name="company" v-model="form.company">
                </div>
                
                <div class="form-group">
                    <label for="message">Message *</label>
                    <textarea id="message" name="message" v-model="form.message" required></textarea>
                </div>
                
                
                
                <button type="submit" class="submit-btn" :disabled="submitting">{{ submitting ? 'Sending...' : 'Send Message' }}</button>
            </form>
        </div>
    </div>

    <footer>
        <p>&copy; 2026 ProductsDB<span class="reg">®</span>. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
export default {
  name: "Landing",
  data() {
    return {
      days: "00", hours: "00", minutes: "00", seconds: "00",
      countdownTimer: null,
      modalOpen: false,
      modalTitle: "Get in Touch",
      modalSubtitle: "Fill out the form below and we'll get back to you shortly.",
      requestType: "General Inquiry",
      form: { name: "", email: "", company: "", message: "" },
      submitting: false,
      formMsg: "",
      formOk: false,
    };
  },
  methods: {
    openModal(type) {
      if (type === "demo") {
        this.modalTitle = "Request a Demo";
        this.modalSubtitle = "Let us show you how ProductsDB® can transform your product data management.";
        this.requestType = "Demo Request";
      } else if (type === "early-access") {
        this.modalTitle = "Request Early Access";
        this.modalSubtitle = "Be among the first to experience ProductsDB® when we launch.";
        this.requestType = "Early Access Request";
      } else {
        this.modalTitle = "Get in Touch";
        this.modalSubtitle = "Fill out the form below and we'll get back to you shortly.";
        this.requestType = "General Inquiry";
      }
      this.formMsg = "";
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
      this.form = { name: "", email: "", company: "", message: "" };
      this.formMsg = "";
    },
    updateCountdown() {
      const launch = new Date("2026-12-30T00:00:00").getTime();
      const d = launch - Date.now();
      const p = (n) => String(n).padStart(2, "0");
      if (d < 0) { this.days = this.hours = this.minutes = this.seconds = "00"; return; }
      this.days = p(Math.floor(d / 86400000));
      this.hours = p(Math.floor((d % 86400000) / 3600000));
      this.minutes = p(Math.floor((d % 3600000) / 60000));
      this.seconds = p(Math.floor((d % 60000) / 1000));
    },
    async handleSubmit() {
      this.submitting = true;
      this.formMsg = "";
      try {
        const r = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_key: "d5c27cb1-9de7-4cfa-b15a-7c716482bfe1",
            subject: "ProductsDB® - " + this.requestType,
            from_name: this.form.name,
            email: this.form.email,
            message:
              "Name: " + this.form.name +
              "\nEmail: " + this.form.email +
              "\nCompany: " + (this.form.company || "Not provided") +
              "\nRequest Type: " + this.requestType +
              "\n\nMessage:\n" + this.form.message,
          }),
        });
        const res = await r.json();
        if (res.success) {
          this.formOk = true;
          this.formMsg = "Thank you! Your message has been sent successfully. We'll get back to you soon.";
          this.form = { name: "", email: "", company: "", message: "" };
          setTimeout(() => this.closeModal(), 2000);
        } else { throw new Error("fail"); }
      } catch (e) {
        this.formOk = false;
        this.formMsg = "Oops! Something went wrong. Please try again or email us directly at info@brandclubwholesale.com";
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.updateCountdown();
    this.countdownTimer = setInterval(this.updateCountdown, 1000);
  },
  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  },
};
</script>

<style scoped>
* {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        .landing-page {
            --primary: #2563eb;
            --primary-dark: #1e40af;
            --secondary: #8b5cf6;
            --accent: #06b6d4;
            --dark: #0f172a;
            --dark-light: #1e293b;
            --gray: #64748b;
            --light: #f8fafc;
        }

        .landing-page {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: var(--dark);
            overflow-x: hidden;
        }

        /* Header */
        header {
            background: var(--dark);
            color: white;
            padding: 1.5rem 0;
            position: fixed;
            width: 100%;
            top: 0;
            z-index: 1000;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        nav {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .nav-right {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .logo {
            font-size: 1.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, var(--primary), var(--accent));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .nav-links {
            display: flex;
            gap: 2rem;
            list-style: none;
        }

        .nav-links a {
            color: white;
            text-decoration: none;
            transition: color 0.3s;
        }

        .nav-links a:hover {
            color: var(--accent);
        }

        .cta-button {
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: white;
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            transition: transform 0.3s, box-shadow 0.3s;
        }

        .cta-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
        }

        /* Login buttons (route to the existing ProductsDB app login) */
        .login-link {
            color: #e5e7eb;
            background: transparent;
            padding: 0.7rem 1.4rem;
            margin-right: 0.75rem;
            border: 1.5px solid rgba(255, 255, 255, 0.25);
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            transition: color 0.3s, border-color 0.3s, transform 0.3s;
        }
        .login-link:hover {
            color: var(--primary);
            border-color: var(--primary);
            transform: translateY(-2px);
        }
        .btn-secondary {
            color: #e5e7eb;
            background: transparent;
            padding: 1rem 2.5rem;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            font-size: 1.1rem;
            display: inline-block;
            transition: color 0.3s, border-color 0.3s, transform 0.3s;
        }
        .btn-secondary:hover {
            color: var(--primary);
            border-color: var(--primary);
            transform: translateY(-2px);
        }

        /* Hero Section */
        .hero {
            background: linear-gradient(135deg, var(--dark) 0%, var(--dark-light) 100%);
            color: white;
            padding: 10rem 2rem 6rem;
            margin-top: 80px;
            position: relative;
            overflow: hidden;
        }

        .hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: 
                radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
        }

        .hero-content {
            max-width: 1200px;
            margin: 0 auto;
            position: relative;
            z-index: 1;
            text-align: center;
        }

        .hero h1 {
            font-size: 3.5rem;
            font-weight: 800;
            margin-bottom: 1.5rem;
            line-height: 1.2;
            color: #ffffff;
        }

        .hero h1 .brand {
            background: linear-gradient(135deg, var(--primary), var(--accent));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .hero p {
            font-size: 1.5rem;
            margin-bottom: 3rem;
            color: #94a3b8;
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;
        }

        .hero-buttons {
            display: flex;
            gap: 1.5rem;
            justify-content: center;
            flex-wrap: wrap;
        }

        .btn-primary {
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: white;
            padding: 1rem 2.5rem;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            font-size: 1.1rem;
            transition: transform 0.3s, box-shadow 0.3s;
            display: inline-block;
        }

        .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(37, 99, 235, 0.4);
        }

        .btn-secondary {
            background: transparent;
            color: white;
            padding: 1rem 2.5rem;
            border: 2px solid white;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            font-size: 1.1rem;
            transition: all 0.3s;
            display: inline-block;
        }

        .btn-secondary:hover {
            background: white;
            color: var(--dark);
        }

        /* Features Section */
        .features {
            padding: 6rem 2rem;
            background: var(--light);
        }

        .features-content {
            max-width: 1200px;
            margin: 0 auto;
        }

        .section-header {
            text-align: center;
            margin-bottom: 4rem;
        }

        .section-header h2 {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--dark);
        }

        .section-header p {
            font-size: 1.2rem;
            color: var(--gray);
        }

        .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2.5rem;
        }

        .feature-card {
            background: white;
            padding: 2.5rem;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.05);
            transition: transform 0.3s, box-shadow 0.3s;
        }

        .feature-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }

        .feature-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.8rem;
            margin-bottom: 1.5rem;
        }

        .feature-card h3 {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            color: var(--dark);
        }

        .feature-card p {
            color: var(--gray);
            line-height: 1.8;
        }

        /* CTA Section */
        .cta-section {
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: white;
            padding: 6rem 2rem;
            text-align: center;
        }

        .cta-section h2 {
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
            color: #ffffff;
        }

        .cta-section p {
            font-size: 1.3rem;
            margin-bottom: 2.5rem;
            opacity: 0.95;
        }

        .btn-white {
            background: white;
            color: var(--primary);
            padding: 1rem 2.5rem;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            font-size: 1.1rem;
            transition: transform 0.3s, box-shadow 0.3s;
            display: inline-block;
        }

        .btn-white:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(255, 255, 255, 0.3);
        }

        /* Footer */
        footer {
            background: var(--dark);
            color: white;
            padding: 3rem 2rem;
            text-align: center;
        }

        footer p {
            color: #94a3b8;
        }

        /* Countdown Section */
        .countdown-section {
            background: white;
            padding: 4rem 2rem;
            text-align: center;
        }

        .countdown-section h2 {
            font-size: 2rem;
            margin-bottom: 1rem;
            color: var(--dark);
        }

        .countdown-section p {
            color: var(--gray);
            margin-bottom: 2.5rem;
            font-size: 1.1rem;
        }

        .countdown {
            display: flex;
            justify-content: center;
            gap: 2rem;
            flex-wrap: wrap;
            margin-bottom: 2rem;
        }

        .countdown-item {
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: white;
            padding: 2rem 2.5rem;
            border-radius: 12px;
            min-width: 120px;
            box-shadow: 0 10px 30px rgba(37, 99, 235, 0.2);
        }

        .countdown-value {
            font-size: 3rem;
            font-weight: 700;
            display: block;
            line-height: 1;
        }

        .countdown-label {
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 0.5rem;
            opacity: 0.9;
        }

        /* Contact Form Modal */
        .modal {
            display: none;
            position: fixed;
            z-index: 2000;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.7);
            animation: fadeIn 0.3s;
        }

        .modal.active {
            display: flex;
            justify-content: center;
            align-items: center;
        }

        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }

        .modal-content {
            background: white;
            padding: 3rem;
            border-radius: 16px;
            max-width: 500px;
            width: 90%;
            position: relative;
            animation: slideUp 0.3s;
        }

        @keyframes slideUp {
            from { 
                transform: translateY(50px);
                opacity: 0;
            }
            to { 
                transform: translateY(0);
                opacity: 1;
            }
        }

        .close-modal {
            position: absolute;
            right: 1.5rem;
            top: 1.5rem;
            font-size: 2rem;
            color: var(--gray);
            cursor: pointer;
            line-height: 1;
            transition: color 0.3s;
        }

        .close-modal:hover {
            color: var(--dark);
        }

        .modal-content h2 {
            font-size: 2rem;
            margin-bottom: 0.5rem;
            color: var(--dark);
        }

        .modal-content p {
            color: var(--gray);
            margin-bottom: 2rem;
        }

        .form-group {
            margin-bottom: 1.5rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            color: var(--dark);
            font-weight: 600;
        }

        .form-group input,
        .form-group textarea {
            width: 100%;
            padding: 0.875rem;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-size: 1rem;
            transition: border-color 0.3s;
            font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus {
            outline: none;
            border-color: var(--primary);
        }

        .form-group textarea {
            resize: vertical;
            min-height: 120px;
        }

        .submit-btn {
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: white;
            padding: 1rem 2rem;
            border: none;
            border-radius: 8px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            width: 100%;
            transition: transform 0.3s, box-shadow 0.3s;
        }

        .submit-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
        }

        .submit-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
        }

        .form-message {
            padding: 1rem;
            border-radius: 8px;
            margin-bottom: 1rem;
            display: none;
        }

        .form-message.success {
            background: #d1fae5;
            color: #065f46;
            border: 1px solid #10b981;
        }

        .form-message.error {
            background: #fee2e2;
            color: #991b1b;
            border: 1px solid #ef4444;
        }

        .form-message.active {
            display: block;
        }

        /* Responsive */
        @media (max-width: 768px) {
            .hero h1 {
                font-size: 2.5rem;
            }

            .hero p {
                font-size: 1.2rem;
            }

            .nav-links {
                display: none;
            }

            .hero-buttons {
                flex-direction: column;
                align-items: center;
            }

            .features-grid {
                grid-template-columns: 1fr;
            }

            .countdown-item {
                padding: 1.5rem 2rem;
                min-width: 100px;
            }

            .countdown-value {
                font-size: 2.5rem;
            }
        }
    
        .landing-page { min-height: 100vh; }
        .reg {
            font-size: 0.28em;
            vertical-align: super;
            font-weight: 700;
            -webkit-text-fill-color: currentColor;
        }
</style>
